import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  ENUM_OPTIONS,
  FIELD_LABELS,
  humanize,
  STATIC_FORMATS,
  TaggedCreative,
  TagField,
  VIDEO_FORMATS,
  VocabValue,
} from './types';

interface Props {
  creative: TaggedCreative | null;
  vocabulary: VocabValue[];
  onClose: () => void;
  onSetTag: (
    creativeKey: string,
    field: TagField,
    value: string | string[] | null,
  ) => Promise<void>;
}

const SINGLE_FIELDS: TagField[] = [
  'persona',
  'lead_selling_point',
  'hook_device',
  'persuasion_angle',
  'format',
  'presenter',
  'offer_type',
  'awareness_level',
];

// What the AI saw, and one dropdown per tag. Changing a dropdown saves a
// correction immediately; "Undo" drops the correction so the AI's tag shows again.
export default function TagEditorDialog({
  creative,
  vocabulary,
  onClose,
  onSetTag,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [savingField, setSavingField] = useState<TagField | null>(null);
  if (!creative) return null;

  const approved = vocabulary.filter(v => v.status === 'approved');
  const personaOptions = approved.filter(v => v.dimension === 'persona');
  const pointOptions = approved.filter(
    v => v.dimension === 'selling_point' && v.product === creative.product,
  );

  const optionsFor = (field: TagField): { value: string; label: string }[] => {
    if (field === 'persona')
      return personaOptions.map(v => ({ value: v.value_key, label: v.label }));
    if (
      field === 'lead_selling_point' ||
      field === 'supporting_selling_points'
    ) {
      return pointOptions.map(v => ({ value: v.value_key, label: v.label }));
    }
    const values =
      field === 'format'
        ? creative.is_video
          ? VIDEO_FORMATS
          : STATIC_FORMATS
        : ENUM_OPTIONS[field] || [];
    return values.map(v => ({ value: v, label: humanize(v) }));
  };

  const save = async (field: TagField, value: string | string[] | null) => {
    setSavingField(field);
    setError(null);
    try {
      await onSetTag(creative.creative_key, field, value);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingField(null);
    }
  };

  const obs = creative.observation;

  // Shown under every corrected tag; the supporting list is a tag like the rest.
  const undoButton = (field: TagField) =>
    creative.overridden.includes(field) ? (
      <Button
        size="small"
        sx={{ p: 0, minWidth: 0, fontSize: '0.75rem' }}
        disabled={savingField !== null}
        onClick={() => save(field, null)}
      >
        Undo correction (show AI's tag)
      </Button>
    ) : null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {creative.product} · {humanize(creative.media_type)}
        {creative.spend != null && (
          <Typography
            component="span"
            variant="body2"
            sx={{ color: '#64748B', ml: 1 }}
          >
            ${Math.round(creative.spend).toLocaleString()} lifetime spend
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ width: 240, flexShrink: 0 }}>
          {creative.image_proxy_url ? (
            <Box
              component="img"
              src={creative.image_proxy_url}
              alt=""
              sx={{
                width: '100%',
                borderRadius: 2,
                border: '1px solid #E2E8F0',
              }}
            />
          ) : (
            <Box
              sx={{
                height: 240,
                borderRadius: 2,
                border: '1px dashed #E2E8F0',
              }}
            />
          )}
          {creative.is_video && (
            <Typography
              variant="caption"
              sx={{ color: '#64748B', display: 'block', mt: 0.5 }}
            >
              Video: the AI watched and listened to the whole ad. The image is
              its cover frame.
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 280 }}>
          {creative.status === 'failed' ? (
            <Typography variant="body2" sx={{ color: '#DC2626' }}>
              The AI couldn't read this creative: {creative.error_message}
            </Typography>
          ) : (
            obs && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="overline"
                  sx={{ color: '#64748B', fontWeight: 600 }}
                >
                  What the AI saw
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {obs.summary}
                </Typography>
                <Typography variant="body2">
                  <strong>Who it's for:</strong> {obs.targetPersona}
                </Typography>
                {obs.personaEvidence && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#64748B', fontSize: '0.8125rem' }}
                  >
                    {obs.personaEvidence}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Claims:</strong> {obs.sellingPoints.join(' · ')}
                </Typography>
              </Box>
            )
          )}

          <Typography
            variant="overline"
            sx={{ color: '#64748B', fontWeight: 600 }}
          >
            Tags
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5,
              mt: 0.5,
            }}
          >
            {SINGLE_FIELDS.map(field => {
              const options = optionsFor(field);
              const current = (creative.tags[field] as string | null) || '';
              const known = options.some(o => o.value === current);
              return (
                <Box key={field}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label={FIELD_LABELS[field]}
                    value={known ? current : ''}
                    disabled={
                      savingField !== null ||
                      creative.status === 'failed' ||
                      !options.length
                    }
                    helperText={
                      !options.length ? 'Nothing approved yet' : undefined
                    }
                    onChange={e => save(field, e.target.value)}
                  >
                    {options.map(o => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {undoButton(field)}
                </Box>
              );
            })}
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                select
                size="small"
                fullWidth
                label={FIELD_LABELS.supporting_selling_points}
                SelectProps={{ multiple: true }}
                value={creative.tags.supporting_selling_points || []}
                disabled={
                  savingField !== null ||
                  creative.status === 'failed' ||
                  !pointOptions.length
                }
                helperText="Up to 2"
                onChange={e => {
                  const value = e.target.value as unknown as string[];
                  if (value.length <= 2)
                    save('supporting_selling_points', value);
                  else setError('Pick at most 2 supporting selling points.');
                }}
              >
                {optionsFor('supporting_selling_points').map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              {undoButton('supporting_selling_points')}
            </Box>
          </Box>
          {error && (
            <Typography variant="body2" sx={{ color: '#DC2626', mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
